import { useEffect, useState } from "react";
import { api, fmtCurrency } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trophy, Fuel, Leaf, Users, Loader2 } from "lucide-react";

/**
 * Driver League Table — top-N fuel users ranked by litres consumed, with
 * cost and CO2e alongside. Data comes from `ampol_vehicle_entries` (driver
 * name captured in the raw Ampol Report parser since Iteration 52). Users
 * can flip between a specific month and FYTD.
 */
export function DriverLeagueTab({ reports = [] }) {
  const [scope, setScope] = useState("fytd");   // "fytd" or a YYYY-MM
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = scope === "fytd" ? {} : { period_month: scope };
    api.get("/sustainability/ampol/driver-league", { params })
      .then((r) => setData(r.data))
      .catch(() => setData({ top_drivers: [], fleet: {} }))
      .finally(() => setLoading(false));
  }, [scope]);

  const drivers = data?.top_drivers || [];
  const fleet = data?.fleet || {};

  return (
    <div className="space-y-4" data-testid="sust-driver-league-tab">
      {/* Header + scope selector */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            Driver League — Top {drivers.length || 20} Fuel Users
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Ranked by total litres. Sourced from the Ampol Report raw
            data (driver name field). Useful for spotting outliers and
            driver-training candidates.
          </p>
        </div>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-40 text-xs" data-testid="driver-league-scope-select">
            <SelectValue placeholder="Scope" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fytd">FYTD (Jul–Jun)</SelectItem>
            {reports.map((r) => (
              <SelectItem key={r.report_month} value={r.report_month}>
                {r.report_month}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Fleet KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total Litres" value={`${(fleet.total_litres || 0).toLocaleString()} L`} icon={Fuel} tone="slate" />
        <Kpi label="Fleet Cost (Inc GST)" value={fmtCurrency(fleet.total_cost_inc_gst || 0)} icon={Fuel} tone="slate" />
        <Kpi label="Fleet CO2e" value={`${((fleet.total_co2e_kg || 0) / 1000).toFixed(2)} t`} icon={Leaf} tone="emerald" />
        <Kpi label="Active Drivers" value={fleet.driver_count || 0} icon={Users} tone="slate" />
      </div>

      {/* Table */}
      <div className="border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-xs" data-testid="driver-league-table">
          <thead>
            <tr className="bg-slate-100 text-slate-700">
              <th className="text-left px-3 py-2 w-10">#</th>
              <th className="text-left px-3 py-2">Driver</th>
              <th className="text-right px-3 py-2">Litres</th>
              <th className="text-right px-3 py-2">Cost (Inc GST)</th>
              <th className="text-right px-3 py-2">CO2e (kg)</th>
              <th className="text-right px-3 py-2">Fills</th>
              <th className="text-left px-3 py-2">Vehicles</th>
              <th className="text-left px-3 py-2">Fuel</th>
              <th className="text-right px-3 py-2">vs Avg</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="text-center py-6 text-slate-500">
                <Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Loading…
              </td></tr>
            )}
            {!loading && drivers.length === 0 && (
              <tr><td colSpan={9} className="text-center py-6 text-slate-500">
                No driver data yet — upload an Ampol Report on the Ampol tab.
              </td></tr>
            )}
            {!loading && drivers.map((d, i) => {
              const avg = fleet.avg_litres_per_driver || 0;
              const vsAvg = avg > 0 ? ((d.total_litres - avg) / avg) * 100 : 0;
              const tone = vsAvg > 50 ? "text-rose-600"
                        : vsAvg > 20 ? "text-amber-600"
                        : vsAvg < -20 ? "text-emerald-600"
                        : "text-slate-500";
              return (
                <tr key={d.driver} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-slate-500">{i + 1}</td>
                  <td className="px-3 py-2 font-medium">{d.driver}</td>
                  <td className="px-3 py-2 text-right">{d.total_litres.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">{fmtCurrency(d.total_cost_inc_gst)}</td>
                  <td className="px-3 py-2 text-right">{d.total_co2e_kg.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">{d.trip_count}</td>
                  <td className="px-3 py-2 text-slate-600 text-[11px]">
                    {(d.vehicles || []).slice(0, 3).join(", ")}
                    {(d.vehicles || []).length > 3 && ` +${d.vehicles.length - 3}`}
                  </td>
                  <td className="px-3 py-2 text-slate-600 text-[11px]">
                    {(d.fuel_types || []).join(", ")}
                  </td>
                  <td className={`px-3 py-2 text-right font-medium ${tone}`}>
                    {vsAvg >= 0 ? "+" : ""}{vsAvg.toFixed(0)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400">
        "vs Avg" compares each driver's total litres against the fleet average per driver for the same period.
        Red = &gt;50% above average · Amber = 20–50% above · Green = &gt;20% below (efficient).
      </p>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone = "slate" }) {
  const toneMap = {
    slate: "text-slate-700",
    emerald: "text-emerald-700",
  };
  return (
    <div className="border border-slate-200 rounded p-3 bg-white">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className={`text-lg font-semibold mt-1 ${toneMap[tone] || toneMap.slate}`}>{value}</div>
    </div>
  );
}
